import {
  EKeyInitiativeOutcomeValueType,
  IKeyInitiativeOutcomeItem,
  TKeyInitiativeOutcomeItemValue,
} from '../models/keyInitiativeOutcomeItem'

type TJsonValue = string | number | boolean | null | TJsonValue[] | TJsonObject

interface TJsonObject {
  [key: string]: TJsonValue
}

const isJsonObject = (value: TJsonValue): value is TJsonObject => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export const isKeyInitiativeOutcomeValueType = (
  value: string
): value is EKeyInitiativeOutcomeValueType => {
  switch (value) {
    case EKeyInitiativeOutcomeValueType.Financial:
    case EKeyInitiativeOutcomeValueType.Number:
    case EKeyInitiativeOutcomeValueType.Percentage:
    case EKeyInitiativeOutcomeValueType.FreeText:
      return true

    default:
      return false
  }
}

const getOutcomeValueType = (value: TJsonValue): EKeyInitiativeOutcomeValueType => {
  return typeof value === 'string' && isKeyInitiativeOutcomeValueType(value)
    ? value
    : EKeyInitiativeOutcomeValueType.FreeText
}

export const normalizeKeyInitiativeOutcomeItemValue = (
  value: TKeyInitiativeOutcomeItemValue,
  valueType: EKeyInitiativeOutcomeValueType
): TKeyInitiativeOutcomeItemValue => {
  if (valueType === EKeyInitiativeOutcomeValueType.FreeText) {
    return value === null ? '' : String(value)
  }

  if (value === null) {
    return null
  }

  const stringValue = typeof value === 'string' ? value.trim() : null
  if (stringValue === '') {
    return null
  }

  const normalizedValue = typeof value === 'number' ? value : Number(stringValue)
  if (!Number.isFinite(normalizedValue) || normalizedValue < 0) {
    return null
  }

  if (valueType === EKeyInitiativeOutcomeValueType.Percentage && normalizedValue > 100) {
    return null
  }

  return normalizedValue
}

const mapJsonObjectToOutcomeItem = (item: TJsonObject): IKeyInitiativeOutcomeItem => {
  const valueType = getOutcomeValueType(item.valueType)
  const name = typeof item.name === 'string' ? item.name : ''
  const rawValue =
    typeof item.value === 'string' || typeof item.value === 'number' ? item.value : null

  return {
    name,
    valueType,
    value: normalizeKeyInitiativeOutcomeItemValue(rawValue, valueType),
  }
}

const createLegacyTextOutcomeItem = (value: string): IKeyInitiativeOutcomeItem => ({
  name: '',
  valueType: EKeyInitiativeOutcomeValueType.FreeText,
  value,
})

export const createKeyInitiativeOutcomeItem = (): IKeyInitiativeOutcomeItem => ({
  name: '',
  valueType: EKeyInitiativeOutcomeValueType.FreeText,
  value: '',
})

export const parseKeyInitiativeOutcome = (value?: string | null): IKeyInitiativeOutcomeItem[] => {
  const rawValue = value?.trim() ?? ''
  if (!rawValue) {
    return []
  }

  let parsedValue: TJsonValue
  try {
    parsedValue = JSON.parse(rawValue) as TJsonValue
  } catch {
    return [createLegacyTextOutcomeItem(value ?? '')]
  }

  if (typeof parsedValue === 'string') {
    return [createLegacyTextOutcomeItem(parsedValue)]
  }

  if (!Array.isArray(parsedValue)) {
    return [createLegacyTextOutcomeItem(value ?? '')]
  }

  return parsedValue.filter(isJsonObject).map(mapJsonObjectToOutcomeItem)
}

export const serializeKeyInitiativeOutcome = (items: IKeyInitiativeOutcomeItem[]): string => {
  return JSON.stringify(items)
}

export const normalizeKeyInitiativeOutcome = (value?: string | null): string => {
  return serializeKeyInitiativeOutcome(parseKeyInitiativeOutcome(value))
}
